import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { adminAxios } from "../../axiosInstance";

const fetchStoreHealth = async () => {
  const response = await adminAxios.get("analytics/store/health");
  return response?.data?.data;
};

const GREEN = { gradient: "linear-gradient(135deg,#16a34a,#22d3ee)", glow: "#22c55e33", text: "#16a34a" };
const AMBER = { gradient: "linear-gradient(135deg,#fde047,#facc15)", glow: "#facc1533", text: "#ca8a04" };
const RED = { gradient: "linear-gradient(135deg,#ef4444,#dc2626)", glow: "#ef444433", text: "#dc2626" };

// Color by label first (Inactive/At risk red, Healthy/Active/Excellent/Good green, anything
// else amber); fall back to the score when there is no label
const getHealthColors = (label = "", score) => {
  const key = label.trim().toLowerCase();
  if (["inactive", "at risk"].includes(key)) return RED;
  if (["healthy", "active", "excellent", "good"].includes(key)) return GREEN;
  if (key) return AMBER;
  if (score >= 70) return GREEN;
  if (score >= 40) return AMBER;
  return RED;
};

const LEGEND = [
  { color: GREEN, name: "Healthy / Active", note: "sales are coming in at your usual pace" },
  { color: AMBER, name: "In between", note: "sales are slowing compared with your usual pace" },
  { color: RED, name: "At risk / Inactive", note: "no recent sales compared with your usual pace" },
];

function StoreHealthPanel() {
  const [showHealthModal, setShowHealthModal] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["store-health"],
    queryFn: fetchStoreHealth,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const score = Number(data?.score);
  const colors = getHealthColors(data?.label, score);

  return (
    <div className="flex flex-col bg-white rounded-lg gap-2 p-2 items-center relative">
      <div className="relative w-full mb-2 flex justify-center">
        <div className="flex flex-row items-center justify-center w-full gap-3">
          <h3 className="text-2xl w-full text-start font-medium">Store Health</h3>
          <button
            onClick={() => setShowHealthModal(!showHealthModal)}
            className="text-blue-500 hover:text-blue-700 text-xl pr-2 transition-colors flex items-center"
            aria-label="Store Health Information"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6">
              <circle cx="10" cy="10" r="10" fill="#dbeafe" />
              <text x="50%" y="62%" textAnchor="middle" fontSize="13" fill="#2563eb" fontFamily="Arial" fontWeight="bold">
                i
              </text>
            </svg>
          </button>
        </div>

        {showHealthModal && (
          <>
            <div
              className="fixed inset-0 z-40 bg-gray-900 bg-opacity-40"
              onClick={() => setShowHealthModal(false)}
            />
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 translate-y-16 z-50 w-80"
              style={{ minWidth: "18rem" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white border-4 border-gray-900 rounded-3xl p-5 shadow-2xl relative">
                <h4 className="text-sm font-bold mb-2 text-gray-900">
                  Store Health shows how active your store's sales are, from 0 to 100.
                </h4>
                <p className="text-gray-700 text-xs leading-relaxed mb-4">
                  We look at how many days it has been since your last sale and compare that
                  with how often you usually make a sale. The longer the gap compared with
                  your usual pace, the lower the score.
                </p>
                <div className="space-y-1.5 text-xs">
                  {LEGEND.map(({ color, name, note }) => (
                    <div key={name} className="flex items-start gap-2">
                      <span
                        className="inline-block w-3 h-3 rounded-full mt-0.5 shrink-0"
                        style={{ background: color.gradient }}
                      ></span>
                      <span className="text-gray-800">
                        <span className="font-medium">{name}</span>: {note}
                      </span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setShowHealthModal(false)}
                  className="w-full mt-4 bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold transition-colors shadow-md hover:shadow-lg"
                >
                  Got it
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-32">
          <p>Loading...</p>
        </div>
      ) : isError || !data ? (
        <div className="flex justify-center items-center h-32 text-center">
          <p className="text-red-500">Unable to load store health.</p>
        </div>
      ) : (
        <div className="flex flex-col items-center pb-2 pt-1 w-full">
          <div
            className="w-24 h-24 rounded-full flex items-center justify-center shadow-xl mb-2 border-4 border-white"
            style={{ background: colors.gradient, boxShadow: `0 2px 32px 0 ${colors.glow}` }}
          >
            <span className="text-3xl font-bold text-white drop-shadow-lg select-none">
              {Number.isFinite(score) ? score : "-"}
            </span>
          </div>
          {data.label && (
            <span className="text-lg font-medium" style={{ color: colors.text }}>
              {data.label}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default StoreHealthPanel;
